import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import banIcon from 'assets/svg/ban.svg';
import waitingIcon from 'assets/svg/waiting.svg';
import { H5 } from 'components/typography/Headers';
import { ItemWithAuctionDto } from 'services/item/withAuctionsData';

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 5px;
`;

const Icon = styled.img`
  width: 30px;
  height: 30px;
  margin-bottom: 5px;
`;

const Text = styled(H5)`
  text-align: center;
  color: ${theme.color.white};
  white-space: pre-line;
  text-shadow: -2px -2px 0 ${theme.color.black}, 2px -2px 0 ${theme.color.black}, -2px 2px 0 ${theme.color.black},
    2px 2px 0 ${theme.color.black};
`;

type OverlayIcon = 'Ban' | 'Wait';

export type ItemStatusOverlayConfig = {
  icon: OverlayIcon;
  labelKey: string;
  testId: string;
};

export const resolveItemStatusOverlay = (item?: ItemWithAuctionDto): ItemStatusOverlayConfig | null => {
  if (!item) {
    return { icon: 'Ban', labelKey: 'my-activity.items.item-hidden', testId: 'item-hidden-overlay' };
  }

  if (item.itemStatus === 'PUBLISHED' && item.hidden) {
    return {
      icon: 'Ban',
      labelKey: 'my-activity.items.inappropriate-content',
      testId: 'inappropriate-content-overlay',
    };
  }

  if (item.itemStatus === 'SOLD' && item.auction?.status === 'AWAITING_PAYMENT') {
    return {
      icon: 'Wait',
      labelKey: 'my-activity.items.auction-awaiting-payment',
      testId: 'auction-awaiting-payment-overlay',
    };
  }

  if (item.itemStatus === 'UNSOLD' && !!item.auction?.winningBidId) {
    return { icon: 'Wait', labelKey: 'my-activity.items.auction-canceled', testId: 'auction-canceled-overlay' };
  }

  if (item.itemStatus === 'UNSOLD') {
    return { icon: 'Wait', labelKey: 'my-activity.items.auction-no-bids', testId: 'auction-no-bids-overlay' };
  }

  return null;
};

interface Props {
  item?: ItemWithAuctionDto;
}

export const MyActivityItemStatusOverlay = ({ item }: Props) => {
  const { t } = useTranslation('myProfile');
  const overlay = resolveItemStatusOverlay(item);

  if (!overlay) return null;

  const iconSrc = overlay.icon === 'Wait' ? waitingIcon : banIcon;

  return (
    <Overlay data-testid={overlay.testId}>
      <Icon data-testid={`${overlay.testId}-icon`} src={iconSrc} alt={'status icon'} />
      <Text>{t(overlay.labelKey)}</Text>
    </Overlay>
  );
};
